import memoizeOne from "memoize-one";
import { type ReactNode, useMemo, useState } from "react";
import type { MoveItemArgs } from "../primitives/types";
import { TreeContext, type TreeContextValue } from "./contexts";
import { TreeRootProvider, useTreeRootContext } from "./tree-root-context";

type CleanupFn = () => void;

function createTreeItemRegistry() {
	const registry = new Map<string, { element: HTMLElement }>();

	const registerTreeItem = ({
		itemId,
		element,
	}: {
		itemId: string;
		element: HTMLElement;
		actionMenuTrigger: HTMLElement;
	}): CleanupFn => {
		registry.set(itemId, { element });
		return () => {
			registry.delete(itemId);
		};
	};

	return { registry, registerTreeItem };
}

export type TreeProviderProps = {
	initialOpenState?: Map<string, boolean>;
	onMoveItem?: (args: MoveItemArgs) => void;
	onOpenStateChange?: (itemId: string, isOpen: boolean) => void;
	dragAndDropEnabled?: boolean;
	children: ReactNode;
};

function TreeProviderInner({
	children,
	dragAndDropEnabled,
}: {
	children: ReactNode;
	dragAndDropEnabled: boolean;
}) {
	const rootContext = useTreeRootContext();
	const [{ registerTreeItem }] = useState(createTreeItemRegistry);

	const context = useMemo<TreeContextValue>(
		() => ({
			uniqueContextId: rootContext.uniqueContextId,
			getPathToItem: memoizeOne((targetId: string) =>
				rootContext.getPathToItem(targetId),
			),
			findItemBranch: rootContext.findItemBranch,
			getItem: rootContext.getItem,
			itemHasChildren: rootContext.itemHasChildren,
			onMoveItem: rootContext.onMoveItem,
			subscribeItemOpen: rootContext.subscribeItemOpen,
			getItemOpen: rootContext.getItemOpen,
			registerTreeItem,
			setItemOpen: rootContext.setItemOpen,
			toggleItemOpen: rootContext.toggleItemOpen,
			dragAndDropEnabled,
		}),
		[rootContext, registerTreeItem, dragAndDropEnabled],
	);

	return (
		<TreeContext.Provider value={context}>{children}</TreeContext.Provider>
	);
}

export function TreeProvider({
	initialOpenState,
	onMoveItem,
	onOpenStateChange,
	dragAndDropEnabled = true,
	children,
}: TreeProviderProps) {
	return (
		<TreeRootProvider
			initialOpenState={initialOpenState}
			onMoveItem={onMoveItem}
			onOpenStateChange={onOpenStateChange}
		>
			<TreeProviderInner dragAndDropEnabled={dragAndDropEnabled}>
				{children}
			</TreeProviderInner>
		</TreeRootProvider>
	);
}
